import type React from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { selectConfigFromBootstrap, selectFeatureBehaviorFromBootstrap } from 'store/slice/bootstrap';
import { Optional } from 'core/type/generic';
import { Category, SuggestionResult } from 'core/type/SuggestionResult.types';
import { BootstrapConfig, Geo } from 'core/type/BootstrapConfig.types';
import { closeOverlay, OverlayType, selectTextSearchOverlayVisible } from 'store/slice/overlay';
import { selectIsGlobalKeywordSearchPage, selectPageTypeId } from 'store/slice/navigation';
import { selectTextForTextSearch, selectTextSearchName } from 'store/slice/home';
import { TextSearchType } from 'core/type/TextSearch.types';
import { trackElementEvent } from 'core/messaging/analytics/analytics';
import {
    ActivitiesCloseCustomSheetClickEventActivitiesFieldsV1,
    ActivitiesOverlayTextSearchResultItemClickEventActivitiesFieldsV4,
} from '@agoda/analytics-messages-definition-activities/events.split';
import {
    ActivitiesCloseCustomSheetClickEventActivitiesManifestV1,
    ActivitiesOverlayTextSearchResultItemClickEventActivitiesManifestV4,
} from '@agoda/analytics-messages-definition-activities/manifest/events';
import { isCityPage, isSearchPage } from 'core/constant/analytics/AnalyticsPageType';
import { ActivityCardProps } from 'core/type/ActivityCard.types';
import { FeatureName } from 'core/constant/FeatureName';
import { isB } from 'core/window/bootstrapConfig';
import { FunnelExperimentId } from 'core/type/ExperimentId';

interface StateProps {
    config: BootstrapConfig;
    isTextSearchOverlayVisible: boolean;
    locale: string;
    pageTypeId: number;
    searchText: string;
    isSearchBoxFeatureGroup: boolean;
    isInCitySearch: boolean;
    isHideRecentSearch: boolean;
    suggestionResult?: SuggestionResult;
    isNearByEnabled?: boolean;
    isGlobalKeywordSearch?: boolean;
    isModifyEnterKeyPressForSearchExpOn: boolean;
}

interface OwnProps {
    selectedResult: Optional<SuggestionResult>;
    setSearchText: React.Dispatch<React.SetStateAction<string>>;
    setSelectedResult: (selected: SuggestionResult, category?: Category) => void;
    onClose?: () => void;
    popularActivities?: ActivityCardProps[];
    onKeywordResultSelect?: (keyword: string, geo: Geo) => void;
    onGlobalKeywordResultSelect?: (keyword: string) => void;
    isDisabledGlobalKeywordSearch?: boolean;
}

interface DispatchProps {
    closeTextSearchOverlay: () => void;
    onClickBackTextSearchOverlay: () => void;
    trackTextSearchSelect: (selected: SuggestionResult, query: string) => void;
}

export type TextSearchOverlayProps = OwnProps & StateProps & DispatchProps;

export const mapStateToProps = createSelector(
    selectPageTypeId,
    selectConfigFromBootstrap,
    selectTextForTextSearch,
    selectTextSearchName,
    selectTextSearchOverlayVisible,
    selectFeatureBehaviorFromBootstrap,
    selectIsGlobalKeywordSearchPage,
    (pageTypeId, config, searchedCityName, searchedTerm, isTextSearchOverlayVisible, featureBehaviors, isGlobalKeywordSearchPage): StateProps => {
        const isSearchBoxFeatureGroup = featureBehaviors.SearchBoxFeatureGroup;
        const isGlobalKeywordSearchEnabled = featureBehaviors.GlobalKeywordSearch;
        const isInCitySearch = (isSearchPage(pageTypeId) || isCityPage(pageTypeId)) && isSearchBoxFeatureGroup && (!isGlobalKeywordSearchEnabled || !isGlobalKeywordSearchPage);
        const isCityNamePlaceHolder = featureBehaviors.ActivitiesCityNamePlaceholderBehavior;
        const searchText = isGlobalKeywordSearchEnabled && isGlobalKeywordSearchPage ? searchedTerm : searchedCityName;
        return {
            config,
            pageTypeId,
            isTextSearchOverlayVisible,
            searchText: isCityNamePlaceHolder ? '' : searchText,
            isSearchBoxFeatureGroup,
            locale: config.language.locale,
            suggestionResult: config.geo,
            isInCitySearch,
            isNearByEnabled: featureBehaviors.SearchBoxNearMeFeature,
            isHideRecentSearch: featureBehaviors[FeatureName.ActivitiesRecentSearch],
            isGlobalKeywordSearch: isGlobalKeywordSearchEnabled,
            isModifyEnterKeyPressForSearchExpOn: isGlobalKeywordSearchEnabled && isB(FunnelExperimentId.ACT_5328, config.experiments),
        };
    },
);

export const mapDispatchToProps = (dispatch: Function): DispatchProps => ({
    closeTextSearchOverlay: () => {
        dispatch(closeOverlay(OverlayType.TextSearch));
    },
    onClickBackTextSearchOverlay: () => {
        trackElementEvent<ActivitiesCloseCustomSheetClickEventActivitiesFieldsV1>(ActivitiesCloseCustomSheetClickEventActivitiesManifestV1.ids, {});
        dispatch(closeOverlay(OverlayType.TextSearch));
    },
    trackTextSearchSelect: (selected: SuggestionResult, query: string) => {
        const activityId = selected.activityId || undefined;
        const type = TextSearchType[selected.type];
        trackElementEvent<ActivitiesOverlayTextSearchResultItemClickEventActivitiesFieldsV4>(
            ActivitiesOverlayTextSearchResultItemClickEventActivitiesManifestV4.ids,
            { search_phrase: query },
            {
                city_id: selected.cityId,
                country_code_iso2: selected.countryISO2 && selected.countryISO2.toUpperCase(),
                activity_id: activityId,
                action_element_value: type,
                supplier_id: selected.supplierId,
            },
        );
    },
});
